/**
 * Pastille qu'on peut ranger une fois lue.
 *
 * La croix n'apparaît **qu'au survol** et au focus clavier : une pastille
 * d'information n'a pas à porter en permanence un bouton qui la concurrence du
 * regard, et le nombre de croix visibles sur une liste de dix applications ferait
 * ressembler l'écran à une boîte de réception.
 *
 * Elle reste néanmoins atteignable au clavier — `focus-within` la révèle — sans
 * quoi la fonction n'existerait que pour la souris.
 */

import { X } from 'lucide-react';
import { useState } from 'react';

import { browserStorage, dismiss, isDismissed } from '../lib/dismissed';
import { Badge, FOCUS_RING, type Tone } from './ui';

export interface DismissibleBadgeProps {
  children: React.ReactNode;
  tone?: Tone;
  /**
   * Clé de mémorisation. `null` rend la pastille non masquable — utile pour une
   * mention qui doit rester, et pour éviter une clé inventée qui masquerait tout
   * ce qui lui ressemble.
   */
  storageKey: string | null;
  /** Décrit ce qu'on masque, pour le lecteur d'écran. */
  label: string;
}

export function DismissibleBadge({ children, tone, storageKey, label }: DismissibleBadgeProps) {
  const [storage] = useState(browserStorage);
  const [masque, setMasque] = useState(() => isDismissed(storage, storageKey));

  if (masque) return null;

  return (
    <span className="group/pastille relative inline-flex items-center focus-within:pr-0">
      <Badge {...(tone ? { tone } : {})}>{children}</Badge>
      {storageKey ? (
        <button
          type="button"
          onClick={() => {
            dismiss(storage, storageKey);
            setMasque(true);
          }}
          aria-label={`Masquer « ${label} »`}
          title="Masquer"
          className={`ml-1 hidden rounded p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-900 group-hover/pastille:inline-flex group-focus-within/pastille:inline-flex dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100 ${FOCUS_RING}`}
        >
          <X className="h-3 w-3" aria-hidden="true" />
        </button>
      ) : null}
    </span>
  );
}
